import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';

interface EditDefaultClidModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (clid: string) => void;
  initialClid?: string | null;
  folderName?: string;
  loading?: boolean;
}

const EditDefaultClidModal: React.FC<EditDefaultClidModalProps> = ({
  open,
  onClose,
  onSubmit,
  initialClid,
  folderName,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [clid, setClid] = useState('');
  const [validationError, setValidationError] = useState('');

  // Reset CLID when modal opens or initialClid changes
  useEffect(() => {
    if (open) {
      setClid(initialClid || '');
      setValidationError('');
    }
  }, [open, initialClid]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setClid(value);
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError('');
    }
  };

  // Validate CLID format - phone numbers using DTMF keys only
  const validateClid = (clidValue: string): string | null => {
    if (!clidValue.trim()) {
      return null; // Empty is valid (for deletion)
    }
    
    // CLID should only contain the 12 DTMF keys: 0-9, *, #
    const validClidPattern = /^[0-9*#]*$/;
    
    if (!validClidPattern.test(clidValue)) {
      return 'CLID contains invalid characters. Only DTMF keys (0-9, *, #) are allowed.';
    }
    
    // Check for reasonable length (phone numbers shouldn't be extremely long)
    if (clidValue.length > 20) {
      return 'CLID is too long. Maximum 20 characters allowed.';
    }
    
    return null; // Valid
  };

  const handleSubmit = () => {
    const trimmedClid = clid.trim();
    const error = validateClid(trimmedClid);
    
    if (error) {
      setValidationError(error);
      return;
    }
    
    setValidationError('');
    onSubmit(trimmedClid);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !loading) {
      handleSubmit();
    }
  };

  const handleCancel = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleCancel}>
      <DialogTitle>
        Edit Default CLID {folderName ? `for '${folderName}'` : ''}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="clid"
          name="clid"
          label={t("Default CLID")}
          type="text"
          fullWidth
          variant="outlined"
          value={clid}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          disabled={loading}
          error={Boolean(validationError)}
          helperText={validationError || "Leave empty to remove the default CLID from this folder"}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={loading}>
          {t('Cancel')}
        </Button>
        <Button 
          onClick={handleSubmit} 
          disabled={loading}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {loading ? 'Saving...' : t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDefaultClidModal;